import { useCallback, useEffect, useRef, useState } from "react";
import { useApi } from "../../api";
import { PageHeader } from "../GenericPage";
import { Modal, Notice, Pager } from "../../lib/ui";

/*
 * Product barcode management – port of update_barcode.php (API: backend/inventory.py)
 *  - live search by name, manufacturer or barcode; admins can remove a wrong barcode
 *  - list of products with no barcode and quantity > 1, 10 per page, with a scan box per row
 * After saving a barcode the next row's box gets focus, so a cashier can scan down the list.
 */

function MissingRow({ row, index, autoFocus, onSaved }) {
  const api = useApi();
  const [code, setCode] = useState("");
  const [state, setState] = useState(null); // {tone, text}
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);
  useEffect(() => { if (autoFocus) inputRef.current?.focus(); }, [autoFocus]);

  const save = async (e) => {
    e.preventDefault();
    if (!code.trim()) { setState({ tone: "err", text: "Scan or enter a barcode" }); return; }
    setBusy(true);
    try {
      const r = await api.post("/api/barcodes/update", { product_id: row.product_id, barcode: code });
      onSaved(r.message);
    } catch (err) {
      setState({ tone: "err", text: err.message });
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr>
      <td>{index}</td>
      <td style={{ fontWeight: 600, color: "var(--ink)" }}>{row.product_name}</td>
      <td>{row.manufacturer}</td>
      <td className="num mono">{row.quantity}</td>
      <td>{row.unit_of_measurement}</td>
      <td style={{ minWidth: "16rem" }}>
        <form onSubmit={save} style={{ display: "flex", gap: ".4rem" }}>
          <input ref={inputRef} className="cell" value={code} onChange={(e) => setCode(e.target.value)}
            placeholder="Scan barcode here" aria-label={`Barcode for ${row.product_name}`} autoComplete="off" />
          <button type="submit" className="btn primary sm" disabled={busy}>{busy ? "Saving…" : "Save"}</button>
        </form>
        {state && <small style={{ color: "var(--danger)" }}>{state.text}</small>}
      </td>
    </tr>
  );
}

export default function UpdateBarcode() {
  const api = useApi();
  const [notice, setNotice] = useState(null);
  const [missing, setMissing] = useState(null);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [search, setSearch] = useState(null); // {results, is_admin} | null
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [focusFirst, setFocusFirst] = useState(true);

  const loadMissing = useCallback(async (p) => {
    try {
      const d = await api.get("/api/barcodes/missing", { page: p });
      setMissing(d);
      if (d.page !== p) setPage(d.page);
    } catch (e) { setNotice({ tone: "err", text: e.message }); }
  }, [api]);
  useEffect(() => { loadMissing(page); }, [loadMissing, page]);

  // live search, 300 ms debounce, aborts stale requests
  const runSearch = useCallback(async (term, signal) => {
    setSearching(true); setSearchError(null);
    try {
      const url = new URL(api.base + "/api/barcodes/search");
      url.searchParams.set("q", term);
      const res = await fetch(url, { signal, credentials: "include" });
      const d = await res.json();
      if (!res.ok || d.error) throw new Error(d.error || `server replied ${res.status}`);
      setSearch(d);
    } catch (e) {
      if (e.name !== "AbortError") { setSearchError(e.message); setSearch(null); }
    } finally {
      if (!signal?.aborted) setSearching(false);
    }
  }, [api]);
  useEffect(() => {
    const term = q.trim();
    if (!term) { setSearch(null); setSearchError(null); setSearching(false); return undefined; }
    const ctrl = new AbortController();
    const t = setTimeout(() => runSearch(term, ctrl.signal), 300);
    return () => { clearTimeout(t); ctrl.abort(); };
  }, [q, runSearch]);

  const removeBarcode = async () => {
    const row = confirm;
    setConfirm(null);
    try {
      const r = await api.post("/api/barcodes/remove", { product_id: row.product_id });
      setNotice({ tone: "ok", text: r.message });
      runSearch(q.trim());
      loadMissing(page);
    } catch (e) {
      setNotice({ tone: "err", text: e.message });
    }
  };

  const onSaved = (msg) => {
    setNotice({ tone: "ok", text: msg });
    setFocusFirst(true);
    loadMissing(page);
  };

  return (
    <main className="pg">
      <PageHeader slug="update_barcode" />
      <Notice notice={notice} onClose={() => setNotice(null)} />

      <section className="panel" style={{ marginBottom: "1.25rem" }}>
        <div style={{ padding: "1.1rem 1.25rem .4rem", display: "flex", alignItems: "center", gap: ".6rem" }}>
          <h2 style={{ fontSize: "1rem", margin: 0 }}>Search products and fix wrong barcodes</h2>
          {search?.is_admin && <span className="pill-tag brand">Admin</span>}
        </div>
        <p style={{ margin: "0 1.25rem .8rem", fontSize: ".85rem", color: "var(--muted)" }}>
          Search by product name, manufacturer or barcode. If a product was scanned with the wrong barcode, an admin can remove it
          and the product drops into the missing-barcode list below for re-scanning.
        </p>
        <div className="toolbar" style={{ borderBottom: search || searchError ? undefined : 0 }}>
          <div className="field grow">
            <label htmlFor="bc-q" className="visually-hidden">Search</label>
            <div className="search">
              <i className={`fas ${searching ? "fa-spinner fa-spin" : "fa-magnifying-glass"}`} />
              <input id="bc-q" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Product name, manufacturer or barcode" autoComplete="off" />
            </div>
          </div>
        </div>
        {searchError && <div className="empty" style={{ padding: "1.25rem" }}>{searchError}</div>}
        {search && (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead><tr><th>Product</th><th>Manufacturer</th><th className="num">Qty</th><th>Barcode</th><th>Action</th></tr></thead>
              <tbody>
                {search.results.length === 0 && <tr><td colSpan={5} style={{ color: "var(--muted)" }}>No products match “{q.trim()}”.</td></tr>}
                {search.results.map((r) => (
                  <tr key={r.product_id}>
                    <td style={{ fontWeight: 600, color: "var(--ink)" }}>{r.product_name}</td>
                    <td>{r.manufacturer}</td>
                    <td className="num mono">{r.quantity}</td>
                    <td>{r.barcode ? <span className="mono">{r.barcode}</span> : <span className="pill-tag warn">None</span>}</td>
                    <td>
                      {r.barcode && search.is_admin && (
                        <button type="button" className="btn sm" onClick={() => setConfirm(r)}><i className="fas fa-trash-can" /> Remove barcode</button>
                      )}
                      {r.barcode && !search.is_admin && <span style={{ color: "var(--muted)", fontSize: ".8rem" }}>Admin only</span>}
                      {!r.barcode && <span style={{ color: "#c7c4bc" }}>–</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="panel">
        <div style={{ padding: "1.1rem 1.25rem", borderBottom: "1px solid var(--line)" }}>
          <h2 style={{ fontSize: "1rem", margin: 0 }}>Products missing a barcode</h2>
          <p style={{ margin: ".2rem 0 0", fontSize: ".8125rem", color: "var(--muted)" }}>
            Quantity above 1 only{missing ? ` · ${missing.total} product${missing.total === 1 ? "" : "s"}` : ""}
          </p>
        </div>
        <div className="tbl-wrap">
          <table className="tbl">
            <thead><tr><th>#</th><th>Product</th><th>Manufacturer</th><th className="num">Quantity</th><th>Unit</th><th>New barcode</th></tr></thead>
            <tbody>
              {!missing && [0, 1, 2].map((i) => <tr key={i}>{[0, 1, 2, 3, 4, 5].map((c) => <td key={c}><div className="skel" /></td>)}</tr>)}
              {missing?.rows.map((r, i) => (
                <MissingRow key={r.product_id} row={r} index={missing.offset + i + 1}
                  autoFocus={focusFirst && i === 0} onSaved={onSaved} />
              ))}
            </tbody>
          </table>
          {missing && missing.rows.length === 0 && <div className="empty"><i className="fas fa-circle-check" style={{ color: "var(--up)" }} />All products have barcodes.</div>}
        </div>
        {missing && <Pager page={missing.page} totalPages={missing.total_pages} onPage={(p) => { setFocusFirst(false); setPage(p); }} />}
      </section>

      {confirm && (
        <Modal title="Remove barcode?" onClose={() => setConfirm(null)}
          actions={<><button type="button" className="btn" onClick={() => setConfirm(null)}>Cancel</button>
            <button type="button" className="btn danger" onClick={removeBarcode}>Remove</button></>}>
          Remove barcode <b className="mono">{confirm.barcode}</b> from <b>{confirm.product_name}</b>? This can't be undone.
        </Modal>
      )}
    </main>
  );
}
